import React from 'react';
import { Card, Row, Col, Form, Badge } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { numericKeydownHandler } from '../../../utilities/common';
import { formatNumber } from '../../reports/reportHelper';
import { formatCo2eFactor } from '../scenarioConfig';

const LedLightingForm = ({ baseline, mode }) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const energy = baseline?.data?.energy;

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div className="font-weight-bold mb-3">
            {t('CarbonView Data')}
          </div>

          <Row>
            <Col md={6}>
              <div className="text-muted small">
                {t('Current Lighting Consumption')}
              </div>
              <div className="font-weight-medium">
                {energy
                  ? `${formatNumber(energy.consumption)} ${energy.unit ?? ''}/year`
                  : '...'}
              </div>
            </Col>

            <Col md={6}>
              <div className="text-muted small">
                {t('Grid Emission Factor')}
              </div>
              <div className="font-weight-medium">
                {energy
                  ? `${formatCo2eFactor(energy.emissionFactor)} tCO₂e/${energy.unit ?? ''}`
                  : '...'}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="font-weight-bold">
              {t('User Parameters')}
            </div>
            <Badge variant="success">
              {t('Required')}
            </Badge>
          </div>

          <div className="text-muted small mb-4">
            {t('Define LED lighting specifications and costs')}
          </div>

          {/* Row 1 */}
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('LED Wattage per Fixture (W)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 18')}
                  {...register('ledWattage')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.ledWattage}
                />
                <Form.Text className="text-muted">
                  {t('Power consumption per LED fixture')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.ledWattage?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Number of Fixtures')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 500')}
                  {...register('fixtures')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.fixtures}
                />
                <Form.Text className="text-muted">
                  {t('Total number of LED fixtures to install')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.fixtures?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Operating Hours per Year')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 4380')}
                  {...register('operatingHours')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.operatingHours}
                />
                <Form.Text className="text-muted">
                  {t('Annual operating hours (e.g., 12 hrs/day × 365 days)')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.operatingHours?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('LED Upgrade Cost (£)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 25000')}
                  {...register('upgradeCost')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.upgradeCost}
                />
                <Form.Text className="text-muted">
                  {t('Total cost including fixtures and installation')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.upgradeCost?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Electricity Tariff Rate (£/kWh)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.18')}
                  {...register('tariffRate')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.tariffRate}
                />
                <Form.Text className="text-muted">
                  {t('Current electricity cost per kWh')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.tariffRate?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default LedLightingForm;
